import { Fragment, useState } from "react";
import type { KeyboardEvent, MouseEvent } from "react";
import { cardLangOf, guessedOn, guessedQuestion, publishes } from "@speisekarte/core";
import type { Menu } from "@speisekarte/core";
import { printMenu, printMenuOf, publishPdfSlot } from "../output.ts";
import { outsideOf, pdfMarkOf, renewPlannedPdf, slotPrintNow } from "../outside.ts";
import type { OutsideUse } from "../outside.ts";
import { api, dishesById, messageOf, store, useStore } from "../store.ts";
import type { Data } from "../store.ts";

const listed = (words: string[]) => (words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} und ${words.at(-1)}`);

/** Where guests still see the old state, in words: „mit dem QR-Code und im PDF-Link „Speisekarte““. */
const whereOld = (u: OutsideUse) => (u.kind === "folder" ? `in der ${u.label}` : u.planned ? `im geplanten PDF für „${u.slot?.name ?? "PDF-Link"}“` : u.kind === "qr" ? "mit dem QR-Code" : u.kind === "recommendations" ? "in der Empfehlungskarte" : u.kind === "drinks" ? "in der Getränkekarte im Netz" : u.kind === "pdf" ? `im PDF-Link „${u.slot!.name}“` : "");

/**
 * The marks of a menu in the list (S62, Z-0; colours: Martin, 03.11.2026): green where it stands
 * outside as it is, and where outside shows an old state a mark in the colour of the app – it means
 * „tap me“, as on the buttons (17-ui-konzept § 6a) – with ⚠, which opens the way to renew it.
 */
export function OutsideTags({ uses, onOld }: { uses: OutsideUse[]; onOld: () => void }) {
  const open = (e: MouseEvent | KeyboardEvent) => {
    // The mark sits in the link to the menu: only the way to renew, not the menu.
    e.preventDefault();
    e.stopPropagation();
    onOld();
  };
  // Before each mark a place the line may break, without width (04.11.2026, CI: in a wide font both
  // stood in one line past the edge of the phone, the page wider than the screen). Each mark stays whole.
  return (
    <>
      {uses.map((u) => (
        <Fragment key={u.label}>
          <wbr />
          {u.old ? (
            <span className="tag out-old" role="button" tabIndex={0} title="Draußen steht noch der alte Stand. Antippen zum Erneuern." onClick={open} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && open(e)} data-testid={u.kind === "entrance" ? "entrance-reprint" : u.planned ? `out-old-planned-${u.kind}` : `out-old-${u.kind}`}>
              ⚠ {u.label} ›
            </span>
          ) : (
            <span className={`tag ${u.planned ? "out-planned" : "out"}`} data-testid={u.planned ? `out-planned-${u.kind}` : `out-${u.kind}`}>
              {u.label}
            </span>
          )}
        </Fragment>
      ))}
    </>
  );
}

/**
 * The way to renew what outside shows of a menu (S62, Z-0), after saving it and from its ⚠ mark: what
 * is old, in one sentence, and one button for all that goes on the net – taken over, PDFs made again –
 * and printing for the entrance. Only owner and manager renew the net; the service reads who can.
 * `inline`: in the page after saving (Martin: „an der Karte beim Speichern eine Warnung“), not over it,
 * and gone when nothing is old.
 */
export function OutsideFix({ menu, data, title, onClose, inline = false }: { menu: Menu; data: Data; title: string; onClose: () => void; inline?: boolean }) {
  const role = useStore().role;
  const [state, setState] = useState<{ busy?: boolean; done?: string; error?: string }>({});
  const old = outsideOf(menu, data).filter((u) => u.old);
  const net = old.filter((u) => u.kind === "qr" || u.kind === "recommendations" || u.kind === "drinks");
  const folders = old.filter((u) => u.kind === "folder");
  const pdfs = old.filter((u) => u.kind === "pdf");
  const entrance = old.find((u) => u.kind === "entrance");
  const renew = net.length + pdfs.length + folders.length > 0;
  const may = publishes(role);
  const label = (net.length || folders.length) && pdfs.length ? `Übernehmen und ${pdfs.length === 1 ? "PDF" : "PDFs"} neu erzeugen` : net.length || folders.length ? "Änderungen übernehmen" : pdfs.length === 1 ? "PDF neu erzeugen" : "PDFs neu erzeugen";

  async function run() {
    const pub = data.published ? data.menus.find((m) => m.id === data.published!.menu) : null;
    if (net.length && pub) {
      const guessed = guessedOn(pub, dishesById(data));
      if (guessed > 0 && !confirm(guessedQuestion(guessed))) return;
    }
    setState({ busy: true });
    try {
      if (net.length && pub) await store.change(() => api("/api/publish", { body: { menu: pub.id } }));
      for (const u of folders) await store.change(() => api(`/api/folders/${u.folder}/publish`, { body: {} }));
      for (const u of pdfs) {
        if (u.planned) {
          await renewPlannedPdf(u.planned, store.data ?? data);
          continue;
        }
        const now = slotPrintNow(u.slot!, store.data ?? data);
        if (now) await publishPdfSlot(now, store.data ?? data, u.slot!.id, u.slot!.menuId ?? undefined, pdfMarkOf(now, store.data ?? data, cardLangOf(u.slot!.lang, data.settings.languages ?? [])));
      }
      setState({ done: `Erneuert: Gäste sehen jetzt den neuen Stand ${listed([...net, ...folders, ...pdfs].map(whereOld))}.` });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  async function print() {
    try {
      await printMenu(printMenuOf(menu, data, new Date(), undefined, menu.id), data);
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  if (inline && !old.length && !state.done) return null;
  const body = (
    <>
      {!inline && <h2>{title}</h2>}
        {state.done ? (
          <p className="ok-text" data-testid="outside-fix-done">
            {state.done}
          </p>
        ) : (
          <>
            {renew && (
              <p className="warn-box" data-testid="outside-fix-what">
                Gäste sehen noch den alten Stand {listed([...net, ...folders, ...pdfs].map(whereOld))}.
              </p>
            )}
            {entrance && <p className="warn-box">Der Aushang am Eingang zeigt noch den alten Stand: bitte neu drucken und aushängen.</p>}
            {renew && !may && <p className="small">Erneuern können Inhaber und Verwaltung.</p>}
          </>
        )}
        {state.error && <p className="error-box">{state.error}</p>}
        <div className="buttons">
          {!state.done && renew && may && (
            <button className="primary" onClick={() => void run()} disabled={state.busy} data-testid="outside-fix-run">
              {state.busy ? "Wird erneuert …" : label}
            </button>
          )}
          {!state.done && entrance && (
            <button className={renew && may ? "" : "primary"} onClick={() => void print()} data-testid="outside-fix-print">
              Aushang drucken
            </button>
          )}
          <button onClick={onClose} data-testid="outside-fix-close">
            {state.done ? "Schließen" : "Später"}
          </button>
        </div>
    </>
  );
  return inline ? (
    <section className="card outside-fix-inline" data-testid="outside-fix" aria-label="Draußen erneuern">
      {body}
    </section>
  ) : (
    <div className="overlay" role="dialog" aria-label="Draußen erneuern">
      <div className="dialog" data-testid="outside-fix">
        {body}
      </div>
    </div>
  );
}
